/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';

/**
 * Defines the valid Seatbelt states supported by the SeatbeltIcon component.
 */
export enum SeatbeltStatus {
  Buckled = 'buckled',
  Unbuckled = 'unbuckled',
  Error = 'error',
  Parked = 'parked',
  NotAvailable = 'notAvailable'
}

const CVS_STATUS_MAP: Record<string, SeatbeltStatus> = {
  'safe - buckled'     : SeatbeltStatus.Buckled,
  'unsafe - unbuckled' : SeatbeltStatus.Unbuckled,
  error                : SeatbeltStatus.Error,
  'n/a'                : SeatbeltStatus.NotAvailable
};

/**
 * validateSeatbeltStatus - normalizes the raw seatbelt status received from the
 * CVS into a valid SeatbeltStatus, or undefined when the value is not supported.
 *
 * ```typescript
 *
 * import {validateSeatbeltStatus} from './seatbeltUtils'
 *
 * validateSeatbeltStatus('buckled')   // returns SeatbeltStatus.Buckled
 * validateSeatbeltStatus('UNBUCKLED') // returns SeatbeltStatus.Unbuckled
 * validateSeatbeltStatus('')          // returns undefined
 * validateSeatbeltStatus('foo')       // returns undefined
 *
 * ```
 * @param status - raw seatbelt status
 * @returns - the matching SeatbeltStatus, otherwise undefined
 */
export const validateSeatbeltStatus = (status?: string): SeatbeltStatus | undefined => {
  const normalized = status?.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!normalized) {
    return undefined;
  }

  const statuses = Object.values(SeatbeltStatus) as string[];
  const match = statuses.find(value => value.toLowerCase() === normalized);
  if (match) {
    return match as SeatbeltStatus;
  }

  if (CVS_STATUS_MAP[normalized]) {
    return CVS_STATUS_MAP[normalized];
  }

  // Fallback: CVS returns display text, so exact literals may drift.
  if (normalized.includes('unbuckled')) {
    return SeatbeltStatus.Unbuckled;
  }
  if (normalized.includes('buckled')) {
    return SeatbeltStatus.Buckled;
  }
  if (normalized.includes('parked')) {
    return SeatbeltStatus.Parked;
  }
  if (normalized.includes('error')) {
    return SeatbeltStatus.Error;
  }

  return undefined;
};

/**
 * getSeatbeltStatusLabel - returns the translated label of the given seatbelt
 * status, e.g. 'Safe', N/A for SeatbeltStatus.NotAvailable, or an empty string
 * when the status is not supported.
 *
 * ```typescript
 *
 * import {getSeatbeltStatusLabel} from './seatbeltUtils'
 *
 * getSeatbeltStatusLabel(SeatbeltStatus.Buckled)      // returns 'Safe'
 * getSeatbeltStatusLabel(SeatbeltStatus.NotAvailable) // returns 'N/A'
 * getSeatbeltStatusLabel(undefined)                   // returns ''
 *
 * ```
 * @param status - normalized seatbelt status
 * @returns - the translated label, otherwise an empty string
 */
export const getSeatbeltStatusLabel = (status?: SeatbeltStatus): string => {
  if (!status) {
    return '';
  }

  if (status === SeatbeltStatus.NotAvailable) {
    return translate('common:notAvailable');
  } else {
    return translate(`common:seatbelt.${status}`);
  }
};
